import { Ionicons } from "@expo/vector-icons";
import BottomSheet, {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Pressable, Text, useWindowDimensions, View } from "react-native";
import { remapProps } from "react-native-css-interop";
import ClockIcon from "@/assets/svgs/clock-icon";
import CloseIcon from "@/assets/svgs/close-icon";
import RemoteIcon from "@/assets/svgs/remote-icon";
import ReportIcon from "@/assets/svgs/report-icon";
import UserIcon from "@/assets/svgs/user-icon";
import WarningIcon from "@/assets/svgs/warning-icon";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { useSheet } from "../shared/sheet-provider";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import type { api } from "../utils/convex-api";
import { contactSupport } from "../utils/helpers";
import { SheetIds } from "../utils/sheet-ids";

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type ParentLessonSheetPayload = {
  lessonData: NonNullable<
    typeof api.parent.home.queries.weeklySessions._returnType
  >[0];
};

export type ParentLessonSheetResult = {
  cancelLesson?: boolean;
};

function ParentLessonSheet() {
  const fmt = useFormatDate();
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    ParentLessonSheetPayload,
    ParentLessonSheetResult
  >(SheetIds.PARENT_LESSON);
  const responsiveWidth = useResponsiveWidth();
  const { lessonData } = (sheet?.payload ?? {}) as ParentLessonSheetPayload;
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register],
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss],
  );

  const onClose = useCallback(() => {
    close();
  }, [close]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose],
  );

  const handleJoinIntroCall = useCallback(() => {
    if (lessonData?.url) {
      location.href = lessonData.url;
    }
  }, [lessonData?.url]);

  const isIntroCall = lessonData?.type === "intro_call";
  const isInProgress = lessonData?.status === "in_progress";
  const canJoinIntroCall = isIntroCall && isInProgress && lessonData?.url;

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      {!!lessonData && (
        <BottomSheetView>
          <View className="bg-teachLoop-creamBase rounded-t-[20]">
            <View className="h-6" />
            <View className="mx-5 items-end">
              <Pressable onPress={onClose} className="active:opacity-80">
                <CloseIcon
                  className="size-6"
                  pathClassName="stroke-teachLoop-textPrimary"
                />
              </Pressable>
            </View>
            <View className="mx-5 gap-6">
              <View className="flex-row items-center gap-4">
                <Avatar size="md">
                  <Avatar.Content
                    label={lessonData.tutor.firstName}
                    source={
                      lessonData.tutor.imageUrl
                        ? { uri: lessonData.tutor.imageUrl }
                        : undefined
                    }
                    labelSize="md"
                    size="md"
                  />
                </Avatar>
                <View className="flex-1">
                  <Text className="font-geologica-medium font-medium text-black text-xl leading-[1.5]">
                    {lessonData.subject}
                  </Text>

                  <Text className="font-geologica-regular font-normal text-black text-base leading-[1.5] capitalize">
                    {`${lessonData.tutor.firstName} ${lessonData.tutor.lastName}`}
                  </Text>

                  {lessonData.tutor.email && (
                    <Pressable
                      className="flex-row items-center gap-1 active:opacity-70"
                      onPress={() => {
                        navigator.clipboard.writeText(lessonData.tutor.email!);
                      }}
                    >
                      <Text className="font-geologica-regular font-normal text-gray-600 text-sm leading-[1.5]">
                        {lessonData.tutor.email}
                      </Text>
                      <Ionicons name="copy-outline" size={14} color="#6b7280" />
                    </Pressable>
                  )}
                </View>
              </View>

              <View className="flex-row items-center gap-[6px]">
                {lessonData.deliveryMethod === "in_person" ? (
                  <UserIcon
                    className="size-6"
                    pathClassName={"stroke-gray-800"}
                  />
                ) : (
                  <RemoteIcon
                    className="size-6"
                    pathClassName={"stroke-gray-800"}
                  />
                )}
                <Text
                  className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                >
                  {lessonData.deliveryMethod === "in_person"
                    ? "In person"
                    : "Remote"}
                </Text>
              </View>

              <View className="gap-2">
                <View className="flex-row items-center gap-[6px]">
                  <ClockIcon
                    className="size-6"
                    pathClassName={"stroke-gray-800"}
                  />
                  <Text
                    className={`font-geologica-regular font-normal text-black text-base leading-[1.5]`}
                  >
                    {fmt(lessonData.startTime, "do MMMM")}{" "}
                    {fmt(lessonData.startTime, "HH:mm")} -{" "}
                    {fmt(lessonData.endTime, "HH:mm")}
                  </Text>
                </View>
              </View>

              {lessonData.tutorNotes && (
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                    Tutor Notes
                  </Text>
                  <Text className="font-geologica-regular font-normal text-black text-base leading-[1.5]">
                    {lessonData.tutorNotes}
                  </Text>
                </View>
              )}

              {lessonData.parentNotes && (
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                    Parent Notes
                  </Text>
                  <Text className="font-geologica-regular font-normal text-black text-base leading-[1.5]">
                    {lessonData.parentNotes}
                  </Text>
                </View>
              )}

              {isInProgress && !isIntroCall && (
                <View className="flex-row items-start gap-2 rounded-md bg-yellow-100 px-4 py-3 border border-yellow-300">
                  <WarningIcon
                    pathClassName="fill-yellow-500"
                    className="size-6"
                  />

                  <Text className="text-sm font-geologica-regular font-normal leading-[1.5] text-yellow-800">
                    Important: To participate in the lesson, please access the
                    student's account.
                  </Text>
                </View>
              )}

              <View className="flex-row items-center justify-between">
                <Pressable
                  className="flex-row items-center gap-[6px] active:opacity-80"
                  onPress={contactSupport}
                >
                  <ReportIcon
                    className="size-4"
                    pathClassName={"stroke-teachLoop-accentPrimary"}
                  />
                  <Text
                    className={`font-geologica-medium font-medium text-teachLoop-accentPrimary text-base leading-[1.5]`}
                  >
                    Report a problem
                  </Text>
                </Pressable>

                {canJoinIntroCall && (
                  <Button size="md" onPress={handleJoinIntroCall} icon>
                    <View className="w-[9px] h-[9px] bg-green-400 rounded-full items-center justify-center" />
                    <Button.Text size="md">Join intro call</Button.Text>
                  </Button>
                )}
              </View>

              {lessonData.status === "scheduled" && (
                <View className="items-end">
                  <Button
                    size="sm"
                    outline
                    onPress={() => {
                      close({ cancelLesson: true });
                    }}
                  >
                    <Button.Text size="sm" intent="primary">
                      Cancel lesson
                    </Button.Text>
                  </Button>
                </View>
              )}
            </View>
            <View className="h-8" />
          </View>
        </BottomSheetView>
      )}
    </BottomSheet>
  ) : null;
}

export default ParentLessonSheet;
